import axios from 'axios';
import type { AxiosAdapter, AxiosInstance } from 'axios';
import { createHttp, type EndpointRequestConfig } from './http';
import { createAccountApi } from './requests/account';
import { createAgreementApi } from './requests/agreement';
import { createBillingApi } from './requests/billing';
import { createCommonApi } from './requests/common';
import { createCompanyApi } from './requests/company';
import { createErpApi } from './requests/erp';
import { createOrderApi } from './requests/order';
import { createPlantApi } from './requests/plant';
import { createProductApi } from './requests/product';
import { createReceiptApi } from './requests/receipt';
import { createSubscriptionApi } from './requests/subscription';

export interface CreateHevitonApiV2Options {
  baseURL: string;
  timeoutMs?: number;
  /**
   * access token 게터 — `Authorization: Bearer` 주입. 본 패키지는 데이터 도메인 전용이라
   * 토큰 발급·refresh 는 다루지 않는다 (auth 는 v3 가 소유).
   */
  getAccessToken: () => string | null;
  /**
   * access 재발급 함수 — **composition 이 소유하는 공통 single-flight**. 401 시 인터셉터가 호출해 새
   * access token 을 받고 재시도한다. **null = 재발급 불가(원 요청 실패)** 일 뿐이고, 세션을 끝낼지는
   * 실패 사유를 아는 composition 이 이 함수 안에서 스스로 판단한다. 본 패키지엔 auth 엔드포인트가 없어
   * 재발급 소유는 composition(app-legacy refresh / admin 2.0 reissue) — 세 API 가 같은 함수를 공유한다.
   */
  refreshToken: () => Promise<string | null>;
  /**
   * 세션 확정 만료 통보 — **재발급된 access 마저 401 로 거부된 경우에만** 호출된다.
   * 재발급 실패 자체는 composition 이 사유를 보고 처리하므로 여기로 오지 않는다.
   */
  onAuthError: () => void;
  onMaintenance?: () => void;
  onNetworkError?: () => void;
  onServerError?: (status: number) => void;
  /**
   * 응답 어댑터 override — 지정하면 실제 HTTP 대신 이 함수가 응답을 만든다. 인터셉터(토큰 부여·401
   * 재시도)와 소비처 매퍼는 그대로 타므로 소비처는 실연동과 같은 경로로 동작한다.
   *
   * 소비처 용례: apps/web 의 목데이터 모드(`useMock` — BE 없이 화면 작업·스토어 이미지 촬영),
   * 그리고 테스트에서 고정 응답 주입.
   */
  adapter?: AxiosAdapter;
}

function buildClient(opts: CreateHevitonApiV2Options): AxiosInstance {
  const client = axios.create({
    baseURL: opts.baseURL,
    timeout: opts.timeoutMs ?? 20_000,
    headers: { 'Content-Type': 'application/json' },
    // 주입된 어댑터가 있으면 실제 HTTP 대신 그것이 응답을 만든다(미지정이면 axios 기본 어댑터).
    ...(opts.adapter && { adapter: opts.adapter }),
  });

  // Authorization 헤더 — 판단 근거는 **엔드포인트가 선언한 `requiresAuth`** 하나뿐(`endpoints.ts`,
  // `http.ts` 가 config 에 실어 보냄). true 면 store access 를 무조건 덮어쓴다("상위가 항상 최신 토큰을
  // 들고 있다"가 전제 — 401 재시도도 이 경로로 새 토큰을 받는다). false 면 헤더에 손대지 않는다.
  client.interceptors.request.use((config) => {
    const { endpoint } = config as EndpointRequestConfig;
    if (!endpoint?.requiresAuth) return config;
    const access = opts.getAccessToken();
    if (access) config.headers.set('Authorization', `Bearer ${access}`);
    return config;
  });

  client.interceptors.response.use(
    (res) => res,
    async (error) => {
      if (axios.isCancel(error)) return Promise.reject(error);

      if (!error.response) {
        opts.onNetworkError?.();
        return Promise.reject(error);
      }

      const { status } = error.response;
      const config = error.config as EndpointRequestConfig | undefined;

      // 401 + `retryAfterTokenRefresh` endpoint → 공통 refreshToken(composition single-flight) 후 1회 재시도.
      //
      // ⚠️ 재발급 실패(null)에 **`onAuthError` 를 부르지 않는다** — 세션 종료 판정은 `refreshToken` 소유자
      // (composition)의 몫이다. 이유 둘: (1) 여기선 실패 **사유**를 모른다(401=세션만료 vs 5xx·타임아웃=판정불가).
      // (2) `refreshToken` 은 single-flight 라 1회 도는데 대기 중이던 요청 N개가 각자 부르면 세션 종료가 N번
      // 통보된다. composition 이 사유를 보고 1회만 종료하고, 여기선 원 요청을 실패시키기만 한다.
      if (status === 401 && config?.endpoint?.retryAfterTokenRefresh && !config._retried) {
        config._retried = true;
        const newAccess = await opts.refreshToken();
        if (!newAccess) return Promise.reject(error);
        return client(config); // request interceptor 가 새 access 자동 부여
      }
      // 재시도 후 재-401 = **방금 재발급받은 access 마저 거부** → 세션 확정 만료 → 강제 로그아웃.
      // 재발급이 성공으로 끝나 composition 이 볼 수 없는 사실이라, `onAuthError` 가 남는 유일한 자리다.
      if (status === 401 && config?._retried) {
        opts.onAuthError();
        return Promise.reject(error);
      }

      if (status === 503) {
        opts.onMaintenance?.();
        return Promise.reject(error);
      }
      if (status >= 500) {
        opts.onServerError?.(status);
        return Promise.reject(error);
      }

      return Promise.reject(error);
    },
  );

  return client;
}

export function createHevitonApiV2(opts: CreateHevitonApiV2Options) {
  // request 함수들은 raw client 가 아니라 `http`(Endpoint 래퍼)를 받는다 — 경로 해석 + 정책 전달 일원화.
  const http = createHttp(buildClient(opts));
  return {
    subscription: createSubscriptionApi(http),
    plant: createPlantApi(http),
    account: createAccountApi(http),
    agreement: createAgreementApi(http),
    product: createProductApi(http),
    order: createOrderApi(http),
    billing: createBillingApi(http),
    receipt: createReceiptApi(http),
    common: createCommonApi(http),
    company: createCompanyApi(http),
    // ERP 연동(결의전표) — URL 은 `/api/v4/erp` 지만 같은 BE·같은 host 라 이 클라이언트가 함께 소유한다.
    erp: createErpApi(http),
  };
}

export type HevitonApiV2 = ReturnType<typeof createHevitonApiV2>;
